import { useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import CloseIcon from '@mui/icons-material/Close'
import { Divider } from '@mui/material'
import Grid from '@mui/material/Grid'
import Hidden from '@mui/material/Hidden'
import useScrollPosition from '@react-hook/window-scroll'
import Cookies from 'js-cookie'
import { useSnackbar } from 'notistack'
import qs from 'qs'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import SearchIcon from '../../../icons/SearchIcon'
import useLocalStorage from '../../hooks/useLocalStorage'
import useScrollDirection, { ScrollDirection } from '../../hooks/useScrollDirection'
import { SEARCH_URL } from '../../urls'
import { GlobalDialogFormsParams } from '../GlobalDialogForms'

import HeaderLogo from './components/HeaderLogo'
import HeaderMobileSearch from './components/HeaderMobileSearch'
import HeaderUtilities from './components/HeaderUtilities'
import Menu from './Menu/Menu'
import { isWholesaleModeVar } from '../../../apolloClient'
import { COOKIE_NAMES, LOCAL_STORAGE_KEYS } from '../../../constants'
import { useReactiveVar } from '@apollo/client'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import HeaderSearch from '@components/Header/components/HeaderSearch'
import {
  BACKEND_ENUM_TO_IMG_SIZE,
  DEFAULT_IMG_SIZE,
  REFLECTIONS_IMG_SIZE_ENUM,
  STORAGE_GRID_NAME,
} from '../../Pages/Category/SpecificsList'

const MobileMenu = React.lazy(() => import('./Menu/MobileMenu'))

const HeaderBase = () => {
  const history = useHistory()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const scrollY = useScrollPosition(60)
  const scrollDir = useScrollDirection()
  const [, setParams] = useQueryParams(GlobalDialogFormsParams)
  const parsed = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  })
  const [isActiveOverlay, setIsActiveOverlay] = useState(false)
  const [, setSearchActive, getSearchActive] = useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.SEARCH_ACTIVE, false)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const cartQuery = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default, includeTotalPrice: false },
  })

  const { data: userData, loading: userLoading } = useMeQuery()
  const [searchData, setSearchData] = React.useState((parsed && parsed.q) || '')
  const [, setGridClass] = useLocalStorage<REFLECTIONS_IMG_SIZE_ENUM>(STORAGE_GRID_NAME, DEFAULT_IMG_SIZE)

  useEffect(() => {
    if (userData?.me?.pk) {
      Cookies.remove(COOKIE_NAMES.CART)
    } else {
      if (cartQuery.data?.cart) {
        Cookies.set(COOKIE_NAMES.CART, cartQuery.data?.cart.cookieToken, { sameSite: 'lax' })
      }
    }
  }, [cartQuery.data?.cart, userData?.me])
  useEffect(() => {
    if (!!parsed && !!parsed.q) {
      setSearchActive(!!parsed && !!parsed.q)
    }
  }, [])

  useEffect(() => {
    if (window.dataLayer && !userLoading) {
      window.dataLayer.push({
        event: 'set-user-email',
        email: userData?.me?.email || '',
      })
    }
  }, [userData])

  useEffect(() => {
    if (!userLoading && !userData?.me) {
      isWholesaleModeVar(false)
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)
    }
  }, [userData, userLoading])

  useEffect(() => {
    if (userData?.me?.productListImageSize && window.localStorage.getItem(STORAGE_GRID_NAME) === null) {
      setGridClass(BACKEND_ENUM_TO_IMG_SIZE[userData.me.productListImageSize])
    }
  }, [userData?.me?.productListImageSize])

  useEffect(() => {
    const q = (parsed && parsed.q) || ''
    if (!q) {
      setSearchData('')
    }
    setSearchActive(false)
  }, [location.pathname])

  const onSubmitSearch = (data: { q: string; categoryName: string; categorySlug: string; categoryId: number }) => {
    setSearchData(data.q)
    const search = qs.stringify(
      { q: data.q, categories: data.categoryId ? [`${data.categorySlug}_${data.categoryId}`] : [] },
      {
        addQueryPrefix: true,
        arrayFormat: 'repeat',
      }
    )
    setSearchActive(false)
    history.push({
      pathname: SEARCH_URL,
      search,
    })
  }

  const changeStatusSearchBar = () => {
    setSearchActive(!getSearchActive())
  }

  const handleClickBag = e => {
    e.preventDefault()
    enqueueSnackbar('Your bag is empty', {
      variant: 'info',
    })
  }
  const handleOpenSignInDialog = e => {
    e.preventDefault()
    setParams({ global_dialog_form: 'signIn' }, 'replaceIn')
  }
  const handleClickWSMode = () => {
    isWholesaleModeVar(!isWholesaleMode)
    if (!isWholesaleMode) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE, 'true')
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)
    }
  }
  const isSticky = scrollY > 0

  return (
    <>
      <header
        className={`header w-full${isSticky ? ' header--sticky' : ''}${
          scrollDir === ScrollDirection.DOWN && scrollY > 200 ? ' header--hidden' : ''
        }`}
      >
        {/*<PromoCodes hide={isSticky || isWholesaleMode} />*/}
        <div
          onClick={() => setIsActiveOverlay(false)}
          className={`wrapOverlayGlobal ${isActiveOverlay ? 'active' : ''}`}
        />
        <div className='relative' style={{ zIndex: 500 }}>
          <div className={` bg-white z-50`}>
            <div className='container'>
              {/*<Container maxWidth='xl' disableGutters>*/}
              <div className='mx-auto' style={{ maxWidth: '1680px' }}>
                <Grid container spacing={1}>
                  <Grid item>
                    <div className='flex items-center h-full z-10 relative'>
                      <Hidden lgUp>
                        <React.Suspense
                          fallback={
                            <div id='nav-icon'>
                              <span />
                              <span />
                              <span />
                              <span />
                            </div>
                          }
                        >
                          <MobileMenu />
                        </React.Suspense>
                        {getSearchActive() ? (
                          <CloseIcon onClick={changeStatusSearchBar} className='ml-20' />
                        ) : (
                          <SearchIcon onClick={changeStatusSearchBar} className='ml-20' />
                        )}
                      </Hidden>
                      <Hidden lgDown>
                        <HeaderLogo />
                      </Hidden>
                    </div>
                  </Grid>

                  <Grid item flexGrow={1}>
                    <Hidden lgUp>
                      <div className='flex h-full items-center'>
                        <HeaderLogo />
                      </div>
                    </Hidden>
                    <Hidden lgDown>
                      <div className='h-full items-center'>
                        <HeaderSearch onSubmitSearch={onSubmitSearch} initialQuery={(searchData as string) || ''} />
                      </div>
                    </Hidden>
                  </Grid>
                  <Grid item>
                    <div className={`header-utilities bg-white h-full relative z-10`}>
                      <HeaderUtilities
                        user={userData?.me}
                        cartCount={cartQuery.data?.cart?.count || 0}
                        handleClickSignIn={handleOpenSignInDialog}
                        isStaff={userData?.me?.isStaff}
                        handleClickBag={handleClickBag}
                        setIsActiveOverlay={setIsActiveOverlay}
                        handleClickContactUs={() => setParams({ global_dialog_form: 'contactUs' })}
                        handleClickWSMode={handleClickWSMode}
                        isWholesaleMode={isWholesaleMode}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
              {/*</Container>*/}

              <HeaderMobileSearch
                onSubmitSearch={onSubmitSearch}
                isActive={getSearchActive()}
                initialQuery={(searchData as string) || ''}
                handleToggle={changeStatusSearchBar}
              />
              <Divider />
              <Menu setIsActiveOverlay={setIsActiveOverlay} />
            </div>
          </div>
        </div>
      </header>
      <div className='header--placeholder' />
    </>
  )
}

export default React.memo(HeaderBase)
